/**
 * References panel — popover trigger with grouped entity cards/chips.
 */

import { getUnmatchedReferences } from './reference-utils.js';
import { createEntityChip } from './entity-chip.js';
import { createEntityCard } from './blocks/entity-card.js';
import { t } from '../../../i18n.js';

function humanizeType(type) {
  return type
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function toCardBlock(ref) {
  return {
    type: 'entity_card',
    entityType: ref.entityType,
    entityId: ref.entityId,
    label: ref.label,
    fields: ref.fields ?? [],
    canView: ref.canView,
    canEdit: ref.canEdit,
  };
}

function groupReferences(refs) {
  const map = new Map();
  for (const ref of refs) {
    const key = ref.entityType && ref.entityType !== 'invalid' ? ref.entityType : '';
    const bucket = map.get(key);
    if (bucket) bucket.push(ref);
    else map.set(key, [ref]);
  }
  return Array.from(map.entries());
}

/**
 * @param {Object} options
 * @param {Array} options.references
 * @param {Array} [options.parts]
 * @param {string} [options.fallbackContent]
 * @returns {HTMLElement|null}
 */
export function createReferencesPanel({ references, parts, fallbackContent }) {
  const unmatched = getUnmatchedReferences(references, parts, fallbackContent);
  if (!unmatched.length) return null;

  const count = unmatched.length;
  const triggerLabel =
    count === 1 ? t('ai_related_record_one') : t('ai_related_records_many', { count });

  const panel = document.createElement('div');
  panel.className = 'ai-references-panel';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'ai-references-trigger';
  trigger.setAttribute('aria-haspopup', 'true');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.innerHTML = `
    <svg class="ai-references-trigger__mark" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="4.5" cy="8" r="2.3" stroke="currentColor" stroke-width="1.4"/>
      <circle cx="11.5" cy="8" r="2.3" stroke="currentColor" stroke-width="1.4"/>
      <path d="M6.6 8h2.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
    </svg>
    <span>${triggerLabel}</span>
    <svg class="ai-references-trigger__chevron" width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M3 4.5 6 7.5l3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  const popover = document.createElement('div');
  popover.className = 'ai-references-popover';
  popover.hidden = true;

  const groups = groupReferences(unmatched);
  groups.forEach(([type, refs]) => {
    const group = document.createElement('div');
    group.className = 'ai-references-popover__group';

    if (type) {
      const heading = document.createElement('div');
      heading.className = 'ai-references-popover__heading';
      heading.innerHTML = `<span>${humanizeType(type)}</span>`;
      if (refs.length > 1) {
        const countEl = document.createElement('span');
        countEl.className = 'ai-references-popover__count';
        countEl.textContent = String(refs.length);
        heading.appendChild(countEl);
      }
      group.appendChild(heading);
    }

    const list = document.createElement('ul');
    list.className = 'ai-references-popover__list';

    refs.forEach((ref) => {
      const item = document.createElement('li');
      item.className = 'ai-references-popover__item';
      if (ref.fields?.length) {
        item.appendChild(createEntityCard(toCardBlock(ref)));
      } else {
        item.appendChild(createEntityChip(ref, 'list'));
      }
      list.appendChild(item);
    });

    group.appendChild(list);
    popover.appendChild(group);
  });

  function closePopover() {
    popover.hidden = true;
    trigger.classList.remove('ai-references-trigger--open');
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', onDocClick, true);
  }

  function openPopover() {
    popover.hidden = false;
    trigger.classList.add('ai-references-trigger--open');
    trigger.setAttribute('aria-expanded', 'true');

    const rect = trigger.getBoundingClientRect();
    popover.style.top = `${rect.bottom + 6}px`;
    popover.style.left = `${Math.max(8, rect.left)}px`;

    requestAnimationFrame(() => document.addEventListener('click', onDocClick, true));
  }

  function onDocClick(e) {
    if (!panel.contains(e.target)) closePopover();
  }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (popover.hidden) openPopover();
    else closePopover();
  });

  panel.appendChild(trigger);
  panel.appendChild(popover);
  return panel;
}
